"use client";

import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { getApprovalFormByIdService } from "../../../../Services/approvalFormService";
import { getPriceFormByProjectIdService } from "../../../../Services/priceFormService";
import _ from "lodash";
import { t } from "../../../../utils/translate";
import MainLayout from "../../../components/layout/MainLayout";
import jsPDF from "jspdf";
import "jspdf-autotable";

const ApprovalFormView = () => {
  const currentLanguage = useSelector(
    (state) => state?.global?.current_language
  );
  const { approvalId: approvalFormId } = useParams();
  const [loading, setLoading] = useState(false);
  const [approvalForm, setApprovalForm] = useState({});
  const [editData, setEditData] = useState({});
  const [scaffoldData, setScaffoldData] = useState([]);
  const [scaffoldName, setScaffoldName] = useState([]);
  const [visual, setVisual] = useState([]);
  const [backgroundImage, setBackgroundImage] = useState("");
  const [scaffoldStatus, setScaffoldStatus] = useState("");
  const [userGuideDetail, setUserGuideDetail] = useState({
    enterCalculation: "",
    file: "",
    comment: "",
    isComment: false,
    isFile: false,
  });
  const [newProjectId, setNewProjectId] = useState(null);
  const [rentData, setRentData] = useState([]);
  const [PricesTitle, setPricesTile] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [priceFormLoading, setPriceFormLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    getApprovalFormById();
  }, [approvalFormId]);

  async function getApprovalFormById() {
    try {
      setLoading(true);
      const response = await getApprovalFormByIdService(approvalFormId);
      const data = response?.data?.data[0];
      console.log("data", data);
      setNewProjectId(data?.projectId);
      setUserGuideDetail(data?.userGuideDetail);
      setVisual(data?.visual);
      setScaffoldStatus(data?.status);
      setEditData(data);
      setBackgroundImage(data?.approvalFormImage);

      if (data) {
        const formattedDate = data.date
          ? new Date(data.date).toISOString().slice(0, 10)
          : "";
        const formattedDismantledDate = data.dismantledDate
          ? new Date(data.dismantledDate).toISOString().slice(0, 10)
          : "";

        data.date = formattedDate;
        data.dismantledDate = formattedDismantledDate;
      }

      setScaffoldData(data?.sizeScaffold || []);
      setScaffoldName(data?.scaffoldName || []);
      setApprovalForm(data);
    } catch (Error) {
      return Error;
    } finally {
      setLoading(false);
    }
  }

  const handleScaffoldForm = async (projectId) => {
    setPriceFormLoading(true);

    try {
      const responseData = await getPriceFormByProjectIdService(projectId);

      if (responseData.success) {
        setRentData(responseData?.data?.rent || []);
        if (responseData?.data?.rent?.length > 0) {
          const allKeys = [
            ...new Set(
              responseData?.data?.rent.flatMap((item) =>
                Object.keys(item.prices || {})
              )
            ),
          ];
          const priceKeys = allKeys.filter(
            (key) => key.toLowerCase() !== "volume"
          );
          setPricesTile(priceKeys);

          const dynamicHeaders = [
            "Scaffold Name",
            ...priceKeys.map((key) => `Price (${key})`),
          ];
          setHeaders(dynamicHeaders);
        }
      } else {
        console.error(responseData?.message);
      }
    } catch (error) {
      console.error("Error while fetching scaffold form:", error);
    } finally {
      setPriceFormLoading(false);
    }
  };

  useEffect(() => {
    if (newProjectId) {
      handleScaffoldForm(newProjectId);
    }
  }, [newProjectId]);

  function normalizeUnitType(unitType) {
    const normalized = unitType
      .toLowerCase()
      .replace("²", "2")
      .replace("³", "3")
      .replace("m^2", "m2")
      .replace("m^3", "m3");

    switch (normalized) {
      case "m2":
      case "m²":
        return "m2";
      case "m3":
      case "m³":
        return "m3";
      case "lm":
      case "LM":
        return "lm";
      case "hm":
      case "HM":
        return "hm";
      default:
        return normalized;
    }
  }

  function calculateMeasurement(unitType, size) {
    if (!size || typeof size !== "object") return `0 ${unitType}`;
    const normalizedUnitType =
      unitType === "m³"
        ? "m3"
        : unitType === "m²"
          ? "m2"
          : unitType === "LM"
            ? "lm"
            : unitType === "HM"
              ? "hm"
              : unitType;

    const calculations = {
      m3: () =>
        (Number(size?.length) || 0) *
        (Number(size?.width) || 0) *
        (Number(size?.height) || 0),
      m2: () => (Number(size?.length) || 0) * (Number(size?.width) || 0),
      lm: () => Number(size?.length) || 0,
      hm: () => Number(size?.height) || 0,
      Rent: () => Number(size?.value) || Number(size?.Rent) || 0,
      Volume: () => Number(size?.value) || Number(size?.Volume) || 0,
    };

    const result =
      (calculations[normalizedUnitType]?.() ??
        Number(size?.value) ??
        Number(size?.[normalizedUnitType])) ||
      0;

    return `${result} ${unitType}`;
  }

  function calculateTotal(measurements) {
    const totals = {};

    if (measurements && typeof measurements === "object") {
      Object.keys(measurements).forEach((unitType) => {
        const normalizedUnitType =
          unitType === "m³"
            ? "m3"
            : unitType === "m²"
              ? "m2"
              : unitType === "LM"
                ? "lm"
                : unitType === "HM"
                  ? "hm"
                  : unitType;

        totals[unitType] = measurements[unitType]?.reduce((total, size) => {
          const calculations = {
            m3: () =>
              (Number(size?.length) || 0) *
              (Number(size?.width) || 0) *
              (Number(size?.height) || 0),
            m2: () => (Number(size?.length) || 0) * (Number(size?.width) || 0),
            lm: () => Number(size?.length) || 0,
            hm: () => Number(size?.height) || 0,
            Rent: () => Number(size?.value) || Number(size?.Rent) || 0,
            Volume: () => Number(size?.value) || Number(size?.Volume) || 0,
          };
          const result =
            (calculations[normalizedUnitType]?.() ??
              Number(size?.value) ??
              Number(size?.[normalizedUnitType])) ||
            0;

          return total + result;
        }, 0);
      });
    }

    return Object.fromEntries(
      Object.entries(totals).map(([unit, total]) => [unit, `${total} ${unit}`])
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-500 text-white";
      case "inactive":
        return "bg-red-500 text-white";
      case "disassembled":
        return "bg-orange-500 text-white";
      default:
        return "bg-gray-500 text-white";
    }
  };

  // Enhanced PDF generation function
  const generatePDF = () => {
    // Create a new PDF document
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 15;

    // Set font styles
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);

    // Add main header
    doc.text("SCAFFOLD DETAIL:", 14, yPos);
    yPos += 8;

    // Add project number and scaffold ID in header
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`Project Number: ${editData?.projectNumber || "N/A"}`, 14, yPos);
    
    // Add scaffold ID and Download PDF button area (simulated)
    const rightX = pageWidth - 14;
    doc.setFont("helvetica", "bold");
    doc.setFillColor(0, 114, 187); // Blue background
    doc.roundedRect(rightX - 60, yPos - 8, 25, 8, 2, 2, 'F');
    doc.setTextColor(255, 255, 255); // White text
    doc.setFontSize(8);
    doc.text(`Scff. Id No. ${editData?.scaffoldIdentificationNumber || "N/A"}`, rightX - 47, yPos - 3);
    
    doc.roundedRect(rightX - 32, yPos - 8, 30, 8, 2, 2, 'F');
    doc.text("Download PDF", rightX - 17, yPos - 3);
    
    // Reset text color
    doc.setTextColor(0, 0, 0);
    yPos += 12;

    // Add status badge
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    const statusColor = scaffoldStatus === "active" ? [34, 197, 94] : 
                       scaffoldStatus === "inactive" ? [239, 68, 68] : 
                       scaffoldStatus === "disassembled" ? [249, 115, 22] : [107, 114, 128];
    
    doc.setFillColor(...statusColor);
    doc.roundedRect(14, yPos, 20, 6, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.text(scaffoldStatus || "Status", 16, yPos + 4);
    doc.setTextColor(0, 0, 0);
    yPos += 15;

    // Function to add image to PDF with proper error handling
    const addImageToPdf = (imgSrc, x, y, width, height) => {
      return new Promise((resolve, reject) => {
        if (!imgSrc) {
          resolve();
          return;
        }

        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.onload = () => {
          try {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);

            const imgData = canvas.toDataURL("image/jpeg", 0.8);
            doc.addImage(imgData, "JPEG", x, y, width, height);
            resolve();
          } catch (error) {
            console.error("Error processing image:", error);
            resolve();
          }
        };
        img.onerror = () => {
          console.error("Error loading image:", imgSrc);
          resolve();
        };
        img.src = imgSrc;
      });
    };

    // Store images for later addition at the bottom
    const imagesToAdd = [];

    // Check for form image and store for later
    if (backgroundImage) {
      imagesToAdd.push({
        src: backgroundImage,
        title: "Form Image",
        width: 80,
        height: 50
      });
    }

    // SCAFFOLD INFORMATION SECTION
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Scaffold Information", 14, yPos);
    yPos += 10;

    // Create scaffold information table with better styling
    const scaffoldInfo = [
      ["Work Order Number", editData?.workOrderNumber || "N/A"],
      ["Location", editData?.location || "N/A"],
      ["Build Date", editData?.date ? new Date(editData.date).toLocaleDateString() : "N/A"],
      ["Dismantle Date", editData?.dismantledDate ? new Date(editData.dismantledDate).toLocaleDateString() : "N/A"],
    ];

    doc.autoTable({
      startY: yPos,
      head: [["Field", "Value"]],
      body: scaffoldInfo,
      theme: "grid",
      headStyles: { 
        fillColor: [240, 240, 240], 
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 10
      },
      bodyStyles: {
        fontSize: 9,
        cellPadding: 3
      },
      columnStyles: {
        0: { cellWidth: 50, fontStyle: "bold" },
        1: { cellWidth: 100 }
      },
      margin: { left: 14, right: 14 },
    });

    yPos = doc.lastAutoTable.finalY + 15;

    // Add scaffold names section
    if (scaffoldName?.length > 0) {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Scaffold Names", 14, yPos);
      yPos += 8;

      const scaffoldNamesList = scaffoldName.map((element, index) => [
        index + 1,
        element?.value || "N/A",
        element?.key || "N/A"
      ]);

      doc.autoTable({
        startY: yPos,
        head: [["#", "Name", "Key"]],
        body: scaffoldNamesList,
        theme: "grid",
        headStyles: { 
          fillColor: [240, 240, 240], 
          textColor: [0, 0, 0],
          fontStyle: "bold",
          fontSize: 10
        },
        bodyStyles: {
          fontSize: 9,
          cellPadding: 3
        },
        columnStyles: {
          0: { cellWidth: 15, halign: "center" },
          1: { cellWidth: 70 },
          2: { cellWidth: 50 }
        },
        margin: { left: 14, right: 14 },
      });

      yPos = doc.lastAutoTable.finalY + 15;
    }

    // Check if we need a new page
    if (yPos > pageHeight - 100) {
      doc.addPage();
      yPos = 15;
    }

    // GENERAL INFORMATION SECTION
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("GENERAL INFORMATION:", 14, yPos);
    yPos += 12;

    // Create two-column layout for general information
    const generalInfoLeft = [
      ["Scaffolder Owner", editData?.scaffolderowner || "N/A"],
      ["Built by", editData?.builtBy || "N/A"],
      ["Select Scaffold Class", editData?.scaffoldClass || "N/A"],
      ["Wall Anchors Capacity", `${editData?.wallAnchorsCapacity || "N/A"} ${editData?.AnchorCapacityUnit || ""}`],
    ];

    const generalInfoRight = [
      ["Inspected by", editData?.inspectedBy || "N/A"],
      ["User Responsible", editData?.userResponsible || "N/A"],
      ["Maximum weight per m2 in kilograms", editData?.totalWeightPerM2 || "N/A"],
      ["Amount of Wall Ankers", editData?.amountWallAnkers || "N/A"],
    ];

    // Left column
    doc.autoTable({
      startY: yPos,
      head: [["Field", "Value"]],
      body: generalInfoLeft,
      theme: "grid",
      headStyles: { 
        fillColor: [240, 240, 240], 
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 10
      },
      bodyStyles: {
        fontSize: 9,
        cellPadding: 3
      },
      columnStyles: {
        0: { cellWidth: 40, fontStyle: "bold" },
        1: { cellWidth: 50 }
      },
      margin: { left: 14, right: pageWidth/2 + 5 },
    });

    const leftTableEndY = doc.lastAutoTable.finalY;

    // Right column
    doc.autoTable({
      startY: yPos,
      head: [["Field", "Value"]],
      body: generalInfoRight,
      theme: "grid",
      headStyles: { 
        fillColor: [240, 240, 240], 
        textColor: [0, 0, 0],
        fontStyle: "bold",
        fontSize: 10
      },
      bodyStyles: {
        fontSize: 9,
        cellPadding: 3
      },
      columnStyles: {
        0: { cellWidth: 40, fontStyle: "bold" },
        1: { cellWidth: 50 }
      },
      margin: { left: pageWidth/2 + 5, right: 14 },
    });

    yPos = Math.max(leftTableEndY, doc.lastAutoTable.finalY) + 15;

    // Build according to user guide section
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Build according to user guide", 14, yPos);
    yPos += 8;

    const guideStatus = userGuideDetail?.file || userGuideDetail?.comment ? "No" : "Yes";
    const statusBgColor = guideStatus === "Yes" ? [34, 197, 94] : [239, 68, 68];
    
    doc.setFillColor(...statusBgColor);
    doc.roundedRect(14, yPos, 15, 6, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.text(guideStatus, 16, yPos + 4);
    doc.setTextColor(0, 0, 0);
    yPos += 15;

    // Store user guide file for later addition at bottom
    if (userGuideDetail?.file) {
      doc.setFontSize(12);
      doc.text("User Guide File: (See images section at bottom)", 14, yPos);
      yPos += 8;

      imagesToAdd.push({
        src: userGuideDetail.file,
        title: "User Guide File",
        width: 80,
        height: 50
      });
    }

    // Add user guide comment if available
    if (userGuideDetail?.comment) {
      doc.setFontSize(12);
      doc.text("Comment:", 14, yPos);
      yPos += 5;
      
      const splitComment = doc.splitTextToSize(userGuideDetail.comment, pageWidth - 28);
      doc.setFontSize(10);
      doc.text(splitComment, 14, yPos);
      yPos += splitComment.length * 4 + 10;
    }

    // Check if we need a new page
    if (yPos > pageHeight - 100) {
      doc.addPage();
      yPos = 15;
    }

    // SIZE OF SCAFFOLD TABLE
    if (scaffoldName?.length > 0) {
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Size of Scaffold", 14, yPos);
      yPos += 12;

      // Create detailed scaffold size table matching UI
      const scaffoldTableData = [];
      
      scaffoldName.forEach((scaffold, index) => {
        const scaffoldTitle = `${scaffold?.value} - (${scaffold?.key})`;
        
        // Add scaffold header row
        scaffoldTableData.push([{
          content: scaffoldTitle,
          colSpan: 5,
          styles: { fillColor: [0, 114, 187], textColor: [255, 255, 255], fontStyle: "bold" }
        }]);

        // Add measurements for each unit type
        PricesTitle?.forEach((unitType) => {
          const normalizedType = normalizeUnitType(unitType);
          scaffold?.measurements?.[normalizedType]?.forEach((size, sizeIndex) => {
            let sizeText = "";
            let calculationText = calculateMeasurement(normalizedType, size);

            if (normalizedType === "m3") {
              sizeText = `${size.length || 0} × ${size.width || 0} × ${size.height || 0} (m³)`;
            } else if (normalizedType === "m2") {
              sizeText = `${size.length || 0} × ${size.width || 0} (m²)`;
            } else if (normalizedType === "lm") {
              sizeText = `${size.length || 0} (lm)`;
            } else if (normalizedType === "hm") {
              sizeText = `${size.height || 0} (hm)`;
            } else {
              sizeText = `${size.value || 0} (${unitType})`;
            }

            scaffoldTableData.push([
              index + 1,
              scaffoldTitle,
              sizeText,
              calculationText,
              "" // Total will be filled separately
            ]);
          });

          // Add total row for each unit type
          const totalValue = calculateTotal(scaffold?.measurements)?.[normalizedType] || `0 ${unitType}`;
          if (scaffold?.measurements?.[normalizedType]) {
            scaffoldTableData.push([
              "",
              "",
              `Total (${unitType})`,
              "",
              totalValue
            ]);
          }
        });
      });

      if (scaffoldTableData.length > 0) {
        doc.autoTable({
          startY: yPos,
          head: [["#", "NAME", "SIZE", "CALCULATION", "TOTAL"]],
          body: scaffoldTableData,
          theme: "grid",
          headStyles: { 
            fillColor: [240, 240, 240], 
            textColor: [0, 0, 0],
            fontStyle: "bold",
            fontSize: 9
          },
          bodyStyles: {
            fontSize: 8,
            cellPadding: 2
          },
          columnStyles: {
            0: { cellWidth: 15, halign: "center" },
            1: { cellWidth: 45 },
            2: { cellWidth: 35 },
            3: { cellWidth: 35 },
            4: { cellWidth: 35 }
          },
          margin: { left: 14, right: 14 },
          didParseCell: function(data) {
            // Style total rows differently
            if (data.cell.text[0] && data.cell.text[0].includes('Total')) {
              data.cell.styles.fillColor = [249, 250, 251];
              data.cell.styles.fontStyle = 'bold';
            }
          }
        });

        yPos = doc.lastAutoTable.finalY + 15;
      }
    }

    // Check if we need a new page
    if (yPos > pageHeight - 100) {
      doc.addPage();
      yPos = 15;
    }

    // VISUAL INSPECTION SECTION
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("VISUAL INSPECTION:", 14, yPos);
    yPos += 12;

    if (visual?.length > 0) {
      // Create visual inspection in grid layout (2 columns)
      const visualData = [];
      
      for (let i = 0; i < visual.length; i += 2) {
        const leftItem = visual[i];
        const rightItem = visual[i + 1];
        
        const leftStatus = leftItem?.inspection === "yes" ? "Yes" : 
                          leftItem?.inspection === "no" ? "No" : "N/A";
        const rightStatus = rightItem?.inspection === "yes" ? "Yes" : 
                           rightItem?.inspection === "no" ? "No" : "N/A";
        
        // Add file reference if exists
        const leftFileRef = leftItem?.documentFile ? " (File attached - see images section)" : "";
        const rightFileRef = rightItem?.documentFile ? " (File attached - see images section)" : "";
        
        visualData.push([
          (leftItem?.documentList || "") + leftFileRef,
          leftStatus,
          (rightItem?.documentList || "") + rightFileRef,
          rightStatus || ""
        ]);
        
        // Add comment rows if they exist
        if (leftItem?.documentComment || rightItem?.documentComment) {
          visualData.push([
            leftItem?.documentComment || "",
            "",
            rightItem?.documentComment || "",
            ""
          ]);
        }
      }

      doc.autoTable({
        startY: yPos,
        body: visualData,
        theme: "grid",
        bodyStyles: {
          fontSize: 9,
          cellPadding: 3
        },
        columnStyles: {
          0: { cellWidth: 45 },
          1: { cellWidth: 20, halign: "center" },
          2: { cellWidth: 45 },
          3: { cellWidth: 20, halign: "center" }
        },
        margin: { left: 14, right: 14 },
        didParseCell: function(data) {
          // Color code the status cells
          if (data.cell.text[0] === "Yes") {
            data.cell.styles.fillColor = [34, 197, 94];
            data.cell.styles.textColor = [255, 255, 255];
          } else if (data.cell.text[0] === "No") {
            data.cell.styles.fillColor = [239, 68, 68];
            data.cell.styles.textColor = [255, 255, 255];
          }
        }
      });

      yPos = doc.lastAutoTable.finalY + 15;

      // Store document files for later addition at bottom
      for (let i = 0; i < visual.length; i++) {
        const item = visual[i];
        if (item.documentFile) {
          imagesToAdd.push({
            src: item.documentFile,
            title: `Document for ${item.documentList}`,
            width: 60,
            height: 40
          });
        }
      }
    }

    // Check if we need a new page for comments
    if (yPos > pageHeight - 80) {
      doc.addPage();
      yPos = 15;
    }

    // COMMENTS SECTION
    const commentsData = [
      ["Follow-up Information", editData?.followUp || "No specific follow-up information provided."],
      ["Comments", editData?.comments || "No comments provided."]
    ];

    doc.autoTable({
      startY: yPos,
      body: commentsData,
      theme: "grid",
      bodyStyles: {
        fontSize: 9,
        cellPadding: 4
      },
      columnStyles: {
        0: { cellWidth: 50, fontStyle: "bold", fillColor: [240, 240, 240] },
        1: { cellWidth: 120 }
      },
      margin: { left: 14, right: 14 },
    });

    yPos = doc.lastAutoTable.finalY + 20;

    // Check if we need a new page for signatures
    if (yPos > pageHeight - 100) {
      doc.addPage();
      yPos = 15;
    }

    // SIGNATURES SECTION
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("SIGNATURE:", 14, yPos);
    yPos += 15;

    // Create signature table
    const signatureTableBody = [
      [
        {
          content: "Signature of the Customer",
          styles: { halign: "center", fontStyle: "bold", fontSize: 12 }
        },
        {
          content: "Signature of the Inspector", 
          styles: { halign: "center", fontStyle: "bold", fontSize: 12 }
        }
      ]
    ];

    // Add signature content row with reference to images section
    const customerSigContent = approvalForm?.customerSignature ? 
      "Signature attached (see images section)" : "No signature provided";
    const inspectorSigContent = approvalForm?.inspectorSignature ? 
      "Signature attached (see images section)" : "No signature provided";

    signatureTableBody.push([
      { content: customerSigContent, styles: { halign: "center", minCellHeight: 40 } },
      { content: inspectorSigContent, styles: { halign: "center", minCellHeight: 40 } }
    ]);

    // Add name rows
    const customerName = _.startCase(_.toLower(
      approvalForm?.customerSignatureName || editData?.customerSignatureName || "N/A"
    ));
    const inspectorName = _.startCase(_.toLower(
      approvalForm?.inspectorSignatureName || editData?.inspectorSignatureName || "N/A"
    ));

    signatureTableBody.push([
      { content: customerName, styles: { halign: "center", fontStyle: "bold" } },
      { content: inspectorName, styles: { halign: "center", fontStyle: "bold" } }
    ]);

    doc.autoTable({
      startY: yPos,
      body: signatureTableBody,
      theme: "grid",
      columnStyles: {
        0: { cellWidth: 85 },
        1: { cellWidth: 85 }
      },
      margin: { left: 14, right: 14 },
    });

    yPos = doc.lastAutoTable.finalY + 20;

    // Store signature images for later addition at bottom
    if (approvalForm?.customerSignature) {
      imagesToAdd.push({
        src: approvalForm.customerSignature,
        title: `Customer Signature - ${customerName}`,
        width: 70,
        height: 30
      });
    }

    if (approvalForm?.inspectorSignature) {
      imagesToAdd.push({
        src: approvalForm.inspectorSignature,
        title: `Inspector Signature - ${inspectorName}`,
        width: 70,
        height: 30
      });
    }

    // Add all images at the bottom of the document
    if (imagesToAdd.length > 0) {
      // Check if we need a new page for images section
      if (yPos > pageHeight - 100) {
        doc.addPage();
        yPos = 15;
      }

      // Add images section header
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("IMAGES & DOCUMENTS:", 14, yPos);
      yPos += 15;

      const imagePromises = [];
      let currentImageY = yPos;
      let imagesInCurrentRow = 0;
      const maxImagesPerRow = 2;
      const imageSpacing = 95;

      imagesToAdd.forEach((imageInfo, index) => {
        // Check if we need a new page
        if (currentImageY + imageInfo.height + 25 > pageHeight) {
          doc.addPage();
          currentImageY = 15;
          imagesInCurrentRow = 0;
        }

        // Calculate X position based on images in current row
        const imageX = 14 + (imagesInCurrentRow * imageSpacing);

        // Add image title
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text(imageInfo.title, imageX, currentImageY);

        // Add the image
        imagePromises.push(
          addImageToPdf(imageInfo.src, imageX, currentImageY + 5, imageInfo.width, imageInfo.height)
        );

        imagesInCurrentRow++;

        // Move to next row if we've reached max images per row
        if (imagesInCurrentRow >= maxImagesPerRow) {
          currentImageY += imageInfo.height + 25;
          imagesInCurrentRow = 0;
        }
      });

      // Process all images and then save the PDF
      Promise.all(imagePromises)
        .then(() => {
          // Add footer with date and page numbers
          const totalPages = doc.internal.getNumberOfPages();
          for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(128, 128, 128);
            
            // Add page number
            doc.text(`Page ${i} of ${totalPages}`, pageWidth - 20, pageHeight - 10);
            
            // Add date
            const today = new Date().toLocaleDateString();
            doc.text(`Generated: ${today}`, 14, pageHeight - 10);
          }

          // Save the PDF
          doc.save(`Scaffold_Approval_${editData?.scaffoldIdentificationNumber || approvalFormId}.pdf`);
        })
        .catch((error) => {
          console.error("Error generating PDF with images:", error);
          // Fallback: save PDF without waiting for images
          doc.save(`Scaffold_Approval_${editData?.scaffoldIdentificationNumber || approvalFormId}.pdf`);
        });
    } else {
      // No images to add, save PDF directly
      // Add footer with date and page numbers
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(128, 128, 128);
        
        // Add page number
        doc.text(`Page ${i} of ${totalPages}`, pageWidth - 20, pageHeight - 10);
        
        // Add date
        const today = new Date().toLocaleDateString();
        doc.text(`Generated: ${today}`, 14, pageHeight - 10);
      }

      // Save the PDF
      doc.save(`Scaffold_Approval_${editData?.scaffoldIdentificationNumber || approvalFormId}.pdf`);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <MainLayout title={"Approval View Page"}>
      {loading ? (
        <div className="flex justify-center items-center min-h-[400px]">
          <div
            className="flex flex-col justify-center items-center gap-[10px]"
            role="status"
          >
            <svg
              aria-hidden="true"
              className="inline w-8 h-8 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <h1 className="text-[20px] font-[700] text-[#0072BB]">
              {t("loading")}
            </h1>
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 min-h-screen pb-12">
          <div className="container mx-auto px-4 py-8">
            {/* Header Section */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">
                    {t("scaffoldDetail")}
                  </h1>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-sm font-medium text-gray-500">
                      {t("projectNumber")}: {editData?.projectNumber}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div>
                    <button className="px-3 py-3 rounded-md bg-[#0072BB] text-white font-bold cursor-default">
                      Scff. Id No. {editData?.scaffoldIdentificationNumber}
                    </button>
                  </div>
                  <div
                    className="flex justify-center items-center gap-[1px] bg-[#0072BB] rounded-[5px] px-3 py-2 cursor-pointer hover:bg-blue-700 transition-colors"
                    onClick={generatePDF}
                  >
                    <img
                      src="/pdf_logo.svg"
                      className="w-[20px] md:w-[24px] mr-2"
                      alt="PDF"
                    />
                    <span className="text-white">Download PDF</span>
                  </div>
                </div>
              </div>

              {/* Status Badge */}
              <div className="mb-6">
                <span
                  className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(
                    scaffoldStatus
                  )}`}
                >
                  {scaffoldStatus || "Status"}
                </span>
              </div>

              {/* Background Image */}
              {backgroundImage && (
                <div className="mb-6">
                  <h2 className="text-lg font-semibold mb-2">Form Image</h2>
                  <div className="w-full max-w-md h-auto rounded-md overflow-hidden border border-gray-200">
                    <img
                      src={backgroundImage || "/placeholder.svg"}
                      alt="Approval Form"
                      className="w-full h-auto object-contain"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Scaffold Details */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-bold mb-4">Scaffold Information</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Work Order Number
                  </p>
                  <p className="text-gray-800 border p-3 rounded-md bg-gray-50 mt-1">
                    {editData?.workOrderNumber || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Location</p>
                  <p className="text-gray-800 border p-3 rounded-md bg-gray-50 mt-1">
                    {editData?.location || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Build Date
                  </p>
                  <p className="text-gray-800 border p-3 rounded-md bg-gray-50 mt-1">
                    {editData?.date
                      ? new Date(editData.date).toLocaleDateString()
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Dismantle Date
                  </p>
                  <p className="text-gray-800 border p-3 rounded-md bg-gray-50 mt-1">
                    {editData?.dismantledDate
                      ? new Date(editData.dismantledDate).toLocaleDateString()
                      : "N/A"}
                  </p>
                </div>
              </div>

              {/* Scaffold Names */}
              {scaffoldName?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3">Scaffold Names</h3>
                  <div className="flex flex-wrap gap-2">
                    {scaffoldName.map((element, index) => (
                      <div
                        key={index}
                        className="p-2 rounded flex items-center gap-3 text-[12px] bg-[#0072BB1A]"
                      >
                        <div>
                          {element?.value} - ({element?.key})
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* General Information */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-bold mb-4">
                {t("generalInformation")}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {t("scaffolderOwner")}
                  </p>
                  <p className="text-gray-800 border p-3 rounded-md bg-gray-50 mt-1">
                    {editData?.scaffolderowner || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {t("inspectedBy")}
                  </p>
                  <p className="text-gray-800 border p-3 rounded-md bg-gray-50 mt-1">
                    {editData?.inspectedBy || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {t("builtBy")}
                  </p>
                  <p className="text-gray-800 border p-3 rounded-md bg-gray-50 mt-1">
                    {editData?.builtBy || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {t("userResponsible")}
                  </p>
                  <p className="text-gray-800 border p-3 rounded-md bg-gray-50 mt-1">
                    {editData?.userResponsible || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {t("selectScaffoldClass")}
                  </p>
                  <p className="text-gray-800 border p-3 rounded-md bg-gray-50 mt-1">
                    {editData?.scaffoldClass || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {t("maximumWeightPerm2Inkilograms")}
                  </p>
                  <p className="text-gray-800 border p-3 rounded-md bg-gray-50 mt-1">
                    {editData?.totalWeightPerM2 || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {t("WallAnchorsCapacity")}
                  </p>
                  <p className="text-gray-800 border p-3 rounded-md bg-gray-50 mt-1">
                    {editData?.wallAnchorsCapacity || "N/A"}{" "}
                    {editData?.AnchorCapacityUnit || ""}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {t("amountOfWallAnkers")}
                  </p>
                  <p className="text-gray-800 border p-3 rounded-md bg-gray-50 mt-1">
                    {editData?.amountWallAnkers || "N/A"}
                  </p>
                </div>
              </div>

              {/* User Guide Details */}
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-3">
                  {t("buildAccording")}
                </h3>
                <div className="flex items-center mb-3">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${userGuideDetail?.file || userGuideDetail?.comment
                      ? "bg-red-100 text-red-800"
                      : "bg-green-100 text-green-800"
                      }`}
                  >
                    {userGuideDetail?.file || userGuideDetail?.comment
                      ? t("No")
                      : t("Yes")}
                  </span>
                </div>

                {userGuideDetail?.file && (
                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-500">File</p>
                    <div className="flex items-center gap-2 mt-1">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-blue-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <a
                        href={userGuideDetail.file}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        View File
                      </a>
                    </div>
                  </div>
                )}

                {userGuideDetail?.comment && (
                  <div>
                    <p className="text-sm font-medium text-gray-500">Comment</p>
                    <p className="text-gray-800 border p-3 rounded-md bg-gray-50 mt-1">
                      {userGuideDetail.comment}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Size of Scaffold */}
            {scaffoldName?.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-xl font-bold mb-4">Size of Scaffold</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          #
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Name
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Size
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Calculation
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {scaffoldName?.map((element, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {index + 1}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {element?.value} - ({element?.key})
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {PricesTitle?.map((unitType) => {
                              const normalizedType =
                                normalizeUnitType(unitType);
                              return element?.measurements?.[
                                normalizedType
                              ]?.map((size, sizeIndex) => (
                                <div
                                  key={`${normalizedType}-${sizeIndex}`}
                                  className="mb-2 text-sm"
                                >
                                  {normalizedType === "m3" ? (
                                    <span>
                                      {size.length || 0} × {size.width || 0} ×{" "}
                                      {size.height || 0} (m³)
                                    </span>
                                  ) : normalizedType === "m2" ? (
                                    <span>
                                      {size.length || 0} × {size.width || 0}{" "}
                                      (m²)
                                    </span>
                                  ) : normalizedType === "lm" ? (
                                    <span>{size.length || 0} (lm)</span>
                                  ) : normalizedType === "hm" ? (
                                    <span>{size.height || 0} (hm)</span>
                                  ) : (
                                    <span>
                                      {size.value || 0} ({unitType})
                                    </span>
                                  )}
                                </div>
                              ));
                            })}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {PricesTitle?.map((unitType) => {
                              const normalizedType =
                                normalizeUnitType(unitType);
                              return element?.measurements?.[
                                normalizedType
                              ]?.map((size, sizeIndex) => (
                                <div
                                  key={`${normalizedType}-${sizeIndex}`}
                                  className="border p-1 rounded-xl flex flex-col mb-2 border-[#0072BB] text-sm"
                                >
                                  {calculateMeasurement(normalizedType, size)}
                                </div>
                              ));
                            })}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {PricesTitle?.filter((unitType) => {
                              const normalizedType =
                                normalizeUnitType(unitType);
                              return element?.measurements?.[normalizedType];
                            }).map((unitType) => {
                              const normalizedType =
                                normalizeUnitType(unitType);
                              const totalValue = calculateTotal(
                                element?.measurements
                              )?.[normalizedType];

                              return (
                                <div
                                  key={normalizedType}
                                  className="border p-1 rounded-xl flex flex-col mb-2 border-[#0072BB] text-sm"
                                >
                                  {totalValue || `0 ${unitType}`}
                                </div>
                              );
                            })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Visual Inspection */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-bold mb-4">
                {t("visualInspection")}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {visual?.map((item, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-semibold">{item.documentList}</h3>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${item.inspection === "yes"
                          ? "bg-green-100 text-green-800"
                          : item.inspection === "no"
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-800"
                          }`}
                      >
                        {item.inspection === "yes"
                          ? t("Yes")
                          : item.inspection === "no"
                            ? t("No")
                            : "N/A"}
                      </span>
                    </div>

                    {item.documentFile && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-gray-500 mb-1">
                          Attached File
                        </p>
                        <div className="flex items-center gap-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 text-blue-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          <a
                            href={item.documentFile}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline text-xs"
                          >
                            View File
                          </a>
                        </div>
                      </div>
                    )}

                    {item.documentComment && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">
                          Comment
                        </p>
                        <p className="text-gray-800 border p-2 rounded-md bg-gray-50 text-xs">
                          {item.documentComment}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Comments Section */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h2 className="text-lg font-semibold mb-3">
                    {t("areThereAnySpecific")}
                  </h2>
                  <p className="text-gray-800 border p-3 rounded-md bg-gray-50 min-h-[100px]">
                    {editData?.followUp ||
                      "No specific follow-up information provided."}
                  </p>
                </div>
                <div>
                  <h2 className="text-lg font-semibold mb-3">
                    {t("comments")}
                  </h2>
                  <p className="text-gray-800 border p-3 rounded-md bg-gray-50 min-h-[100px]">
                    {editData?.comments || "No comments provided."}
                  </p>
                </div>
              </div>
            </div>

            {/* Signatures Section */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold mb-4">{t("signature")}</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col items-center border rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">
                    {t("signatureOfTheCustomer")}
                  </h3>
                  {approvalForm?.customerSignature ? (
                    <div className="flex flex-col items-center">
                      <img
                        className="mb-3 border p-2 rounded"
                        width={169}
                        src={
                          approvalForm?.customerSignature || "/placeholder.svg"
                        }
                        alt="Customer Signature"
                      />
                      <p className="font-medium text-gray-800">
                        {_.startCase(
                          _.toLower(
                            approvalForm?.customerSignatureName ||
                            editData?.customerSignatureName
                          )
                        )}
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-[100px] w-full border border-dashed rounded-md bg-gray-50">
                      <p className="text-gray-500">No signature provided</p>
                    </div>
                  )}
                </div>

                <div className="flex flex-col items-center border rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">
                    {t("signatureOfTheInspector")}
                  </h3>
                  {approvalForm?.inspectorSignature ? (
                    <div className="flex flex-col items-center">
                      <img
                        className="mb-3 border p-2 rounded"
                        width={169}
                        src={
                          approvalForm?.inspectorSignature || "/placeholder.svg"
                        }
                        alt="Inspector Signature"
                      />
                      <p className="font-medium text-gray-800">
                        {_.startCase(
                          _.toLower(
                            approvalForm?.inspectorSignatureName ||
                            editData?.inspectorSignatureName
                          )
                        )}
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-[100px] w-full border border-dashed rounded-md bg-gray-50">
                      <p className="text-gray-500">No signature provided</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-center gap-4 mt-6">
              <button onClick={() => handleGoBack()}
                className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-md transition-colors"
              >
                Back to List
              </button>
              <button
                onClick={generatePDF}
                className="px-6 py-3 bg-[#0072BB] hover:bg-blue-700 text-white font-medium rounded-md transition-colors flex items-center gap-2"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default ApprovalFormView;